import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { connectorOrigin, connectorRequest } from './connectorApi';
import '../Profile.css';

// Account usage is authorized once here. Individual searches stay in the assistant.
export default function TalentConnectorConsentPage() {
  const { currentUser } = useAuth();
  const { t, i18n } = useTranslation('profile');
  const [params] = useSearchParams();
  const ticket = params.get('ticket');
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [allowSpending, setAllowSpending] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setDetails(null); setError(''); setAllowSpending(false);
    if (!ticket || !connectorOrigin) {
      setError(!connectorOrigin ? 'not_configured' : 'invalid_request');
      return () => controller.abort();
    }
    connectorRequest(currentUser, `consent?ticket=${encodeURIComponent(ticket)}`, { signal: controller.signal })
      .then(value => { if (!controller.signal.aborted) setDetails(value); })
      .catch(e => { if (!controller.signal.aborted) setError(e.code || 'unavailable'); });
    return () => controller.abort();
  }, [currentUser, ticket]);

  const spends = details?.scopes?.includes('talent:spend');
  const balance = details?.balance;
  async function connect() {
    if (!details || (spends && (!allowSpending || !balance))) return;
    setBusy(true); setError('');
    try {
      const result = await connectorRequest(currentUser, 'consent', { body: { ticket, allow_spending: Boolean(spends && allowSpending) } });
      const expected = new URL(details.redirect_uri);
      const target = new URL(result.redirect_url);
      if (target.origin !== expected.origin || target.pathname !== expected.pathname || !['http:', 'https:'].includes(target.protocol)) throw new Error('Redirect changed');
      window.location.assign(target.href);
    } catch (e) { setError(e.code || 'unavailable'); }
    finally { setBusy(false); }
  }

  const money = cents => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(cents / 100);
  const permissions = { 'talent:read': 'readPermission', 'talent:spend': 'spendPermission', 'talent:save': 'savePermission' };
  return <main className="profile-page-container connector-approval-page" dir={i18n.dir()}>
    <Link to="/profile?tab=connections" className="settings-back-link"><i className="fa-solid fa-arrow-left" aria-hidden="true" /> {t('connector.backToSettings')}</Link>
    <section className="profile-panel connector-approval-card" aria-busy={busy}>
      <span className="connector-approval-icon" aria-hidden="true"><i className="fa-solid fa-link" /></span>
      <h1>{t('connector.consentTitle')}</h1>
      <p className="profile-muted-copy">{currentUser?.email}</p>
      {error && <p role="alert" className="settings-notice">{t(`connector.errors.${error}`, t('connector.errors.unavailable'))}</p>}
      {!details && !error && <p role="status">{t('connector.loading')}</p>}
      {details && <>
        <p>{t('connector.consentDescription', { client: details.client_name })}</p>
        <ul className="connector-permissions">{details.scopes.map(scope => <li key={scope}><i className="fa-solid fa-check" aria-hidden="true" />{t(`connector.${permissions[scope] || 'unknownPermission'}`)}</li>)}</ul>
        {spends && balance && <div className="connector-usage-consent">
          <p>{t('connector.spendingPrices', { search: money(balance.profile_credits * balance.credit_value_cents), contact: money(balance.contact_credits * balance.credit_value_cents) })}</p>
          <p>{t(`connector.spendingMode.${balance.mode}`, { amount: money(balance.mode === 'prepaid' ? balance.prepaid_cents : balance.unpaid_limit_cents) })}</p>
          <label><input type="checkbox" checked={allowSpending} onChange={e => setAllowSpending(e.target.checked)} disabled={busy} /><span>{t('connector.spendingConsent')}</span></label>
          <p className="settings-footnote">{t('connector.spendingHint')}</p>
        </div>}
        <p className="settings-footnote">{t('connector.destination')}: <bdi>{new URL(details.redirect_uri).origin}</bdi></p>
        <div className="connector-approval-actions"><Link className="profile-btn-secondary" to="/profile?tab=connections">{t('cancel')}</Link>
          <button className="profile-btn-primary" type="button" onClick={connect} disabled={busy || (spends && (!allowSpending || !balance))}>{t(busy ? 'connector.working' : 'connector.connectAccount')}</button></div>
      </>}
    </section>
  </main>;
}
